import moment from "moment/moment";
import { isEmpty, isNull, isUndefined } from "lodash";
import {
    APP_URL_ANDROID,
    APP_URL_IOS,
    DEVICE_TYPES,
    DISABLE,
    EDITION,
    ENABLED,
    ENTER_GUEST_NAME_MAX_LENGTH,
    LOCAL_DOMAIN_NAME_FOR_TESTING,
    LOCAL_STORAGE_KEYS,
    PARAMS,
    RECORDING,
    ReservedDomain,
} from "../constants/Constants";
import CustomToast from "../components/UI/customToast";
import { CALL_DATA, GLOBAL_MEETING_DATA, IMAGE_COMPRESSION, PublicVariables } from "../constants/PublicVariables";
import NavigatorService from "../services/navigatorService";
import { PARTICIPANT_TYPE } from "../constants/InviteConstants";
import { CAMERA_FACE, CAMERA_ISSUE_DEVICES } from "../constants/CallSettingConstants";
import { LogFirebase } from "../services/firestore";
import { FIREBASE_EVENT_CATEGORY, FIREBASE_LOG_KEY } from "../constants/FirebaseConstants";
import { REMAINING_FILE_COUNT_TO_MATCH } from "../constants/UploadFilesConstants";
import DetectRTC from "detectrtc";
import { HELP_ARTICLE } from "../constants/LDConstants";
import storage from "../services/storage";

export const isReservedDomain = (domainName) => {
    return ReservedDomain.includes(domainName);
};

export const getDomainURL = () => {
    const currentDomain = window.location.host;
    let customizeDomainUrl = null;

    if (!isReservedDomain(currentDomain)) {
        customizeDomainUrl = window.location.origin;
    }

    if (window.location.hostname === "localhost" || window.location.hostname.includes("ngrok")) {
        customizeDomainUrl = isReservedDomain(LOCAL_DOMAIN_NAME_FOR_TESTING) ? null : "https://" + LOCAL_DOMAIN_NAME_FOR_TESTING;
    }

    return customizeDomainUrl;
};

/* Write all common functions in this file */
export const checkConfigAPIcall = (modifiedDate, storedDate) => {
    if (modifiedDate && storedDate) {
        const newDate = moment.utc(modifiedDate);
        const pastDate = moment.utc(storedDate);
        return newDate.isAfter(pastDate);
    } else {
        return false;
    }
};

// #region -- copy To Clipboard something
/* We are currently utilizing this function. We need to modify it so that it only replaces the code snippet when copying */
const copyLinkToClipboard = (text, tempInput, toastMessage) => {
    navigator.clipboard
        .writeText(text)
        .then(() => {
            if (text !== undefined) {
                CustomToast.success(toastMessage); // We have also added this key with localize
            } else {
                CustomToast.error("Error copying text to clipboard:");
            }
        })
        .catch((error) => {
            CustomToast.error("Error copying text to clipboard:", error);
        });
    document.body.removeChild(tempInput);
};

const sanitizeText = (text) => {
    const safeText = text.trim();
    if (/^javascript:/i.test(safeText)) return ""; // block malicious links
    return safeText;
};

export const CopyTextOrLinkToClipboard = (text, toastMessage) => {
    let range, selection;

    const tempInput = document.createElement("textarea");
    const sanitizedText = sanitizeText(text);
    tempInput.value = sanitizedText;
    document.body.appendChild(tempInput);
    /* If we are copy from ipad and iphone */
    const userAgentRegex = /ipad|iphone/i;
    if (userAgentRegex.exec(navigator.userAgent)) {
        range = document.createRange();
        range.selectNodeContents(tempInput);
        selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        tempInput.setSelectionRange(0, 999999);
        copyLinkToClipboard(sanitizedText, tempInput, toastMessage);
    } else {
        tempInput.select();
        copyLinkToClipboard(sanitizedText, tempInput, toastMessage);
    }
};
//#endregion

/* encodes special char with html entities like & to amp; */
export const encodeCustomHtmlEntities = (str) => {
    if (str) {
        return str.replace(/</g, "&lt;").replace(/>/g, "&gt;");
    }
};

/* decode special char with html entities like & to amp; */
export const decodeCustomHHtmlEntities = (str) => {
    if (str) {
        return str.replace(/&lt;/g, "<").replace(/&gt;/g, ">");
    }
};

const getBrowserDetailObject = (browserName, browserVersion) => {
    return { browserName: browserName, browserVersion: browserVersion };
};

const getBrowserVersion = (reg, index = 1) => {
    const userAgent = navigator.userAgent;

    return reg.exec(userAgent)?.[index];
};

export const getLocalBrowserInfo = () => {
    const localBrowserInfo = {
        browserName: DetectRTC.browser.name || "Unknown",
        browserVersion: DetectRTC.browser.fullVersion || "Unknown",
        isSamsungBrowser: false,
    };

    const userAgent = navigator.userAgent.toLowerCase();

    // Check for Samsung browser on Android
    if (DetectRTC.osName === "Android" && DetectRTC.browser.name === "Chrome") {
        const isSamsung = userAgent.includes("samsungbrowser");

        if (isSamsung) {
            const samsungVersionIndex = userAgent.indexOf("samsungbrowser");
            const samsungVersion = userAgent.substring(samsungVersionIndex + 15, samsungVersionIndex + 19)?.trim();
            localBrowserInfo.browserName = "Samsung Internet";
            localBrowserInfo.browserVersion = samsungVersion;
            localBrowserInfo.isSamsungBrowser = true;
            HELP_ARTICLE.BROWSER_SPECIFIC_SOLUTION_LINK = HELP_ARTICLE.SAMSUNG_INTERNET_ARTICLES_NUMBER;
        } else {
            HELP_ARTICLE.BROWSER_SPECIFIC_SOLUTION_LINK = HELP_ARTICLE.MOBILE_CHROME_ARTICLES_NUMBER;
        }
    } else if (isIOSEdgeBrowser()) {
        // iOS Edge browser - uses WebKit engine, treat like Safari for help articles
        localBrowserInfo.browserName = "Edge";
        localBrowserInfo.browserVersion = getIOSEdgeBrowserVersion();
        HELP_ARTICLE.BROWSER_SPECIFIC_SOLUTION_LINK = HELP_ARTICLE.SAFARI_MOBILE_TAB_ARTICLES_NUMBER;
    } else if (isMicrosoftEdgeBrowser()) {
        // Handle desktop Edge browser
        localBrowserInfo.browserName = "Edge";
        localBrowserInfo.browserVersion = getMicrosoftEdgeBrowserVersion();
    } else {
        if (DetectRTC.osName.toLowerCase() === "ios" || DetectRTC.osName.toLowerCase() === "mac os x") {
            HELP_ARTICLE.BROWSER_SPECIFIC_SOLUTION_LINK = HELP_ARTICLE.SAFARI_MOBILE_TAB_ARTICLES_NUMBER;
        }
        if (localBrowserInfo.browserName.toLowerCase() === "chrome" && DetectRTC.osName.toLowerCase()?.includes("mac")) {
            HELP_ARTICLE.BROWSER_SPECIFIC_SOLUTION_LINK = HELP_ARTICLE.DESKTOP_CHROME_ARTICLES_NUMBER;
        }
    }
    return localBrowserInfo;
};

// Check if running iOS Edge browser (uses EdgiOS identifier)
export const isIOSEdgeBrowser = () => {
    return navigator.userAgent.includes("EdgiOS");
};

// Get iOS Edge browser version
export const getIOSEdgeBrowserVersion = () => {
    const match = navigator.userAgent.match(/EdgiOS\/([\d.]+)/);
    return match ? match[1] : "Unknown";
};

// check is microsoft edge browser or not (desktop Edge)
export const isMicrosoftEdgeBrowser = () => {
    const strUseragent = navigator.userAgent.toLowerCase();
    const regex = /(edg|edge)/;
    const matches = regex.exec(strUseragent);
    // Exclude iOS Edge (EdgiOS) - it's handled separately
    return strUseragent.indexOf("chrome") !== -1 && matches !== null && !isIOSEdgeBrowser();
};

// get microsoft edge browser version
export const getMicrosoftEdgeBrowserVersion = () => {
    let version = null;
    const userAgent = navigator.userAgent.toLowerCase();
    if (isMicrosoftEdgeBrowser()) {
        const regex = /(edg|edge)\/(\d+\.\d+\.\d+\.\d+)/;
        const matches = regex.exec(userAgent);
        if (matches) {
            version = matches[2];
        } else {
            version = DetectRTC.browser.fullVersion;
        }
    }
    return version;
};

export const getExactBrowserName = () => {
    let userAgent = navigator.userAgent;

    if (userAgent.includes("Firefox")) {
        return getBrowserDetailObject("Firefox", getBrowserVersion(/Firefox\/([\d.]+)/));
    }

    if (userAgent.includes("Chrome") && !userAgent.includes("Edg") && !userAgent.includes("SamsungBrowser")) {
        return getBrowserDetailObject("Chrome", getBrowserVersion(/Chrome\/([\d.]+)/));
    }

    if (userAgent.includes("Safari") && !userAgent.includes("Chrome") && !userAgent.includes("CriOS") && !userAgent.includes("EdgiOS")) {
        return getBrowserDetailObject("Safari", getBrowserVersion(/Version\/([\d.]+)/));
    }

    // iOS Edge browser - uses EdgiOS identifier (must check before desktop Edge "Edg")
    if (userAgent.includes("EdgiOS")) {
        return getBrowserDetailObject("Edge", getBrowserVersion(/EdgiOS\/([\d.]+)/));
    }

    if (userAgent.includes("Edg")) {
        return getBrowserDetailObject("Edge", getBrowserVersion(/Edg\/([\d.]+)/));
    }

    if (userAgent.includes("CriOS")) {
        return getBrowserDetailObject("Chrome", getBrowserVersion(/CriOS\/([\d.]+)/));
    }

    if (userAgent.includes("SamsungBrowser")) {
        return getBrowserDetailObject("Samsung Internet", getBrowserVersion(/SamsungBrowser\/([\d.]+)/));
    }

    if (userAgent.includes("MSIE") || userAgent.includes("Trident")) {
        return getBrowserDetailObject("Internet Explorer", getBrowserVersion(/(?:MSIE |Trident\/.*; rv:)([\d.]+)/));
    }

    return getBrowserDetailObject("Unknown", "Unknown");
};

export const getPolicyByLanguage = (currentLanguage, privacyPolicyList) => {
    const policy = privacyPolicyList?.find((policy) => policy?.languageCode === currentLanguage);
    return policy;
};

/**survey redirection on leave button - custom and survey monkey */
export const surveyRedirection = () => {
    const domainName = encodeURI(GLOBAL_MEETING_DATA.domainInfo.domainName);
    const URL =
        GLOBAL_MEETING_DATA.survey.surveyURL +
        "?mid=" +
        GLOBAL_MEETING_DATA.sessionId.toString() +
        "&mref=" +
        CALL_DATA.referenceId +
        "&role=" +
        GLOBAL_MEETING_DATA.survey.inSurveyAgentOrGuestName +
        "&domain=" +
        domainName;

    if (GLOBAL_MEETING_DATA.survey.openNewTab) {
        window.open(URL);
    } else {
        setTimeout(() => {
            window.location.href = URL;
        }, 500);
    }
};

const handleFirebaseLogsForMeetingDetails = () => {
    LogFirebase("MeetingDetail", "companyname", GLOBAL_MEETING_DATA.domainInfo.domainName);
    LogFirebase("MeetingDetail", "cid", GLOBAL_MEETING_DATA.cid);
    LogFirebase("MeetingDetail", "edition", GLOBAL_MEETING_DATA.domainInfo.edition);
    LogFirebase("MeetingDetail", "referenceId", CALL_DATA.referenceId);
    LogFirebase("MeetingDetail", "isAgentForSurvey", GLOBAL_MEETING_DATA.isAgent);
    LogFirebase("MeetingDetail", "openSurvey", GLOBAL_MEETING_DATA.survey.openSurvey);
    LogFirebase("Survey", "surveyType", GLOBAL_MEETING_DATA.survey.surveyType);
    LogFirebase("Survey", "surveyUrl", GLOBAL_MEETING_DATA.survey.surveyURL);
    LogFirebase("MeetingDetail", "smsTemplate", GLOBAL_MEETING_DATA.smsTemplate);

    //// LogFirebase("MeetingDetail","ReleaseVersion", version);
    //// LogFirebase("MeetingDetail","TeamId", MEETINGDETAIL.TEAMID);
    //// LogFirebase("MeetingDetail","TeamName", MEETINGDETAIL.TEAMNAME);
    //// LogFirebase("MeetingDetail","ShowAppVerson", $scope.showAppVersion);APPVERSIONVISIBLE
    //// LogFirebase("MeetingDetail","ShowPowerBy", onloadPoweredBy);POWERBY
    //// if($scope.footerInfo && utility.isset($scope.footerInfo.isFooterVisible)){FOOTERVISIBLE
    ////     LogFirebase("MeetingDetail","FooterVisible", $scope.footerInfo.isFooterVisible);
    //// }
    LogFirebase("MeetingDetail", "remainingFileCount", GLOBAL_MEETING_DATA.remainingFileCount);
};

const getSurveyValueForKey = (joinMeetingData, key, defaultValue) => {
    return joinMeetingData?.survey
        ? joinMeetingData?.isAgent
            ? joinMeetingData?.survey[`agent${key}`]
            : joinMeetingData?.survey[`guest${key}`]
        : defaultValue;
};

export const setGlobalMeetingData = (joinMeetingData) => {
    GLOBAL_MEETING_DATA.sessionId = joinMeetingData?.sessionId;
    CALL_DATA.memberId = joinMeetingData?.memberId;
    GLOBAL_MEETING_DATA.cid = joinMeetingData?.domainId;
    GLOBAL_MEETING_DATA.domainInfo.edition = joinMeetingData?.edition;
    GLOBAL_MEETING_DATA.domainInfo.domainName = joinMeetingData?.domainName;
    GLOBAL_MEETING_DATA.domainInfo.domainId = joinMeetingData?.domainId;
    GLOBAL_MEETING_DATA.domainInfo.domainEmail = joinMeetingData?.domainEmail;
    GLOBAL_MEETING_DATA.domainInfo.domainUrl = joinMeetingData?.domainUrl;
    GLOBAL_MEETING_DATA.isAgent = joinMeetingData?.isAgent;
    GLOBAL_MEETING_DATA.blitzzUserName = joinMeetingData?.ownerName;
    CALL_DATA.memberToken = joinMeetingData?.memberToken;
    GLOBAL_MEETING_DATA.survey.surveyType = getSurveyValueForKey(joinMeetingData, "SurveyType", 1); //// If survey is off, set default value to 1 for survey type as we have no value for type 1
    GLOBAL_MEETING_DATA.survey.openSurvey = getSurveyValueForKey(joinMeetingData, "SurveyOn", false); //// If survey is off, set default value to false for open survey
    GLOBAL_MEETING_DATA.survey.openNewTab = getSurveyValueForKey(joinMeetingData, "OpenNewTab", false); //// If survey is off, set default value to false for open survey in new tab
    GLOBAL_MEETING_DATA.survey.surveyURL = getSurveyValueForKey(joinMeetingData, "SurveyUrl", ""); //// If survey is off, set default value to empty string for survey url
    GLOBAL_MEETING_DATA.survey.inSurveyAgentOrGuestName = joinMeetingData?.isAgent ? "Host" : "Guest";
    GLOBAL_MEETING_DATA.remainingFileCount = joinMeetingData.remainingFileUploadCount;
    CALL_DATA.referenceId = joinMeetingData?.reference || "";
    CALL_DATA.storageType = joinMeetingData?.storageType;
    CALL_DATA.roomSID = !isEmpty(joinMeetingData?.recordingRoomSid) ? joinMeetingData.recordingRoomSid : joinMeetingData?.normalRoomSid;
    handleFirebaseLogsForMeetingDetails();
};

export const isEnterpriseEdition = () => {
    return (
        GLOBAL_MEETING_DATA.domainInfo.edition === EDITION.CONCIERGE_ENTERPRISE ||
        GLOBAL_MEETING_DATA.domainInfo.edition === EDITION.INSPECT_ENTERPRISE
    );
};

export const isProfessionalEdition = () => {
    return (
        GLOBAL_MEETING_DATA.domainInfo.edition === EDITION.CONCIERGE_PROFESSIONAL ||
        GLOBAL_MEETING_DATA.domainInfo.edition === EDITION.INSPECT_PROFESSIONAL
    );
};

export const setImageCompressionValues = (imageCompressionData) => {
    IMAGE_COMPRESSION.FOR_WEB = imageCompressionData.WEB;
    IMAGE_COMPRESSION.WEB_MIN_SIZE = imageCompressionData.WEB_MIN_SIZE;
    IMAGE_COMPRESSION.MAX_IMAGE_SIZE = imageCompressionData.MAX_IMAGE_SIZE;

    LogFirebase(FIREBASE_EVENT_CATEGORY.ImageCompression, FIREBASE_LOG_KEY.CompressionObj, imageCompressionData.WEB);
    LogFirebase(FIREBASE_EVENT_CATEGORY.ImageCompression, FIREBASE_LOG_KEY.MinSize, imageCompressionData.WEB_MIN_SIZE);
    LogFirebase(FIREBASE_EVENT_CATEGORY.ImageCompression, FIREBASE_LOG_KEY.MaxSize, imageCompressionData.MAX_IMAGE_SIZE);
};

export const goToPlayStore = () => {
    if (NavigatorService.isAndroid()) {
        window.location.href = APP_URL_ANDROID;
    } else if (NavigatorService.isIosDevice()) {
        window.location.href = APP_URL_IOS;
    }
};

export const pageUrl = () => {
    return window.location.href;
};

export const generateInviteUrl = (userInviteType) => {
    let url = window.location.href?.split("?")?.[0];
    const searchParams = new URLSearchParams(window.location.search);
    const hasSessionCode = searchParams.get("code"); // for Xiaomi domain

    const smsSupportOption = PublicVariables.featureFlag?.SMSSUPPORTOPTION;
    const showMeMicroPhone = PublicVariables.featureFlag?.SHOWMEMICROPHONE;

    if (hasSessionCode) {
        // for Xiaomi domain
        const sessionId = getSessionId();
        url = url.endsWith("/") ? url.slice(0, -1) : url; // Remove the trailing slash from the URL for the Xiaomi domain.
        if (userInviteType === PARTICIPANT_TYPE.GUEST) {
            url += `?code=` + sessionId + `&${PARAMS.MICROPHONE}=` + showMeMicroPhone + `&${PARAMS.SUPPORT_OPTION}=` + smsSupportOption;
        } else {
            url += `?code=` + sessionId + `&${PARAMS.OBSERVER}=` + 1 + `&${PARAMS.SUPPORT_OPTION}=` + smsSupportOption;
        }
    } else {
        // for all others domain
        if (userInviteType === PARTICIPANT_TYPE.GUEST) {
            url += `?${PARAMS.MICROPHONE}=` + showMeMicroPhone + `&${PARAMS.SUPPORT_OPTION}=` + smsSupportOption;
        } else {
            url += `?${PARAMS.OBSERVER}=` + 1 + `&${PARAMS.SUPPORT_OPTION}=` + smsSupportOption;
        }
    }

    return url;
};

export const isFeatureFlagEnabled = (key) => {
    if (PublicVariables.featureFlag) {
        return PublicVariables.featureFlag[key] === ENABLED;
    }

    return false;
};

export const isFeatureFlagDisabled = (key) => {
    if (PublicVariables.featureFlag) {
        return PublicVariables.featureFlag[key] === DISABLE;
    }

    return false;
};

// check if only upload file option available from the feature flag
export const isOnlyUploadImageAvailable = () => {
    return (
        PublicVariables.featureFlag?.LIVEVIDEO === DISABLE &&
        PublicVariables.featureFlag?.ENABLESCREENSHARE === DISABLE &&
        PublicVariables.featureFlag?.UPLOADFILE === ENABLED
    );
};

export const dualStreamEnabled = () => {
    return PublicVariables.featureFlag?.SHOWMEDUALSTREAM === ENABLED;
};

// check if only upload file option available from the feature flag
export const singleUserEnabled = () => {
    if (PublicVariables.featureFlag?.SHOWMEDUALSTREAM == ENABLED) {
        return false;
    } else if (PublicVariables.featureFlag?.SHOWMEALLOWSINGLEUSER == ENABLED) {
        return true;
    } else {
        return false;
    }
};

/*Show me video enable when Live video is on from CE */
export const showMeVideoEnabled = () => {
    return (
        PublicVariables.featureFlag?.SHOWMEVIDEO &&
        PublicVariables.featureFlag?.SHOWMEVIDEO !== CAMERA_FACE.OFF &&
        PublicVariables.featureFlag?.LIVEVIDEO === ENABLED
    );
};

export const isAgentLocationEnable = (joinMeetingData) => {
    return GLOBAL_MEETING_DATA.isAgent && joinMeetingData?.sessionConfigurations?.AGENTLOCATION === ENABLED;
};

export const isGuestLocationEnable = (joinMeetingData) => {
    return !GLOBAL_MEETING_DATA.isAgent && joinMeetingData?.sessionConfigurations?.ENABLELOCATION === ENABLED;
};

export const isVirtualBackGroundFeatureAvailable = () => {
    return PublicVariables.featureFlag.VIRTUALBACKGROUND === ENABLED && (GLOBAL_MEETING_DATA.isAgent || GLOBAL_MEETING_DATA.isGuest);
};

export const isOfflinePhotoCaptureFeatureAvailable = () => {
    return PublicVariables.featureFlag.OFFLINEPHOTOCAPTURE === ENABLED && (GLOBAL_MEETING_DATA.isAgent || GLOBAL_MEETING_DATA.isGuest);
};

export const remainingCountUpdate = (data) => {
    if (data) {
        GLOBAL_MEETING_DATA.remainingFileCount = data?.remainingFileUploadCount;
    }
};

export const isRemainingFileCountLess = () => {
    return GLOBAL_MEETING_DATA.remainingFileCount < REMAINING_FILE_COUNT_TO_MATCH;
};

export const isMaxLimitReachedForUploadFiles = () => {
    return PublicVariables.isOnlyUploadImageMeet && isProfessionalEdition() && isRemainingFileCountLess();
};

export const decideDeviceType = () => {
    if (NavigatorService.isAndroid()) {
        GLOBAL_MEETING_DATA.device_type = DEVICE_TYPES.ANDROID_BROWSER;
    } else if (PublicVariables.isIOSDevice) {
        document.body?.classList?.add("iosbodyclass");
        GLOBAL_MEETING_DATA.device_type = DEVICE_TYPES.IOS_BROWSER;
    } else {
        GLOBAL_MEETING_DATA.device_type = DEVICE_TYPES.DESKTOP;
    }
};

//// retrun true if current device is Android browser else return false
export const isDeviceTypeAndroidBrowser = () => {
    return GLOBAL_MEETING_DATA.device_type == DEVICE_TYPES.ANDROID_BROWSER;
};

//// return true if current device is iOS browser else return false
export const isDeviceTypeIOSBrowser = () => {
    return GLOBAL_MEETING_DATA.device_type == DEVICE_TYPES.IOS_BROWSER;
};

/*
replace newItem at given index form array
*/
export const replaceAtIndex = (arr, index, newItem) => [...arr.slice(0, index), newItem, ...arr.slice(index + 1)];

/*
insert newItem at given index in array
*/
export const insertAtIndex = (arr, index, newItem) => [...arr.slice(0, index), newItem, ...arr.slice(index)];

export const spliceAtIndex = (arr, index) => [...arr.slice(0, index), ...arr.slice(index + 1)];

/* reverse an array without updating original array */
export const reverseArray = (array) => {
    /* Create a shallow copy of the array using slice() */
    const reversedArray = array.slice();
    /* Reverse the copied array */
    reversedArray.reverse();
    return reversedArray;
};

/*
Check if value is Null or Undefined or Empty or Zero
*/
export const isNullUndefinedEmptyZero = (value) => {
    return value === 0 || isNull(value) || isUndefined(value) || value === "";
};

/* Increase textarea height based on text added in it */
export const adjustTextareaHeight = (element) => {
    element.style.height = "7px"; //Set it to minimum value before setting height
    element.style.height = element.scrollHeight + "px";
};

// Returns the model of the device.
const getDeviceModel = () => {
    const userAgent = navigator.userAgent;
    if (userAgent) {
        const start = userAgent.indexOf("(") + 1;
        const end = userAgent.indexOf(")");
        const str2 = userAgent.substring(start, end);
        const strArr1 = str2.split(";");
        return strArr1[strArr1.length - 1].trim();
    }
};

// Checks if the current device model is in the list of devices that have known camera issues.
export const checkDeviceHasCamIssue = () => {
    const currDevice = getDeviceModel().toUpperCase();
    console.log(currDevice);
    return CAMERA_ISSUE_DEVICES.includes(currDevice);
};

export const setExtraFeatureFlags = (data) => {
    const urlParams = new URLSearchParams(window.location.search);
    PublicVariables.featureFlag = { ...PublicVariables.featureFlag };

    //set grid view/multi stream...if SG on, showme multi stream/grid view off
    if (data?.SHOWMESMARTGLASS == ENABLED) {
        PublicVariables.featureFlag.SHOWMEDUALSTREAM = DISABLE;
        LogFirebase("_DUALSTREAM_DISABLED", "Because Smart Glass support is ON");
    }

    if (PublicVariables.featureFlag?.SHOWMEDUALSTREAM == ENABLED) {
        CALL_DATA.localFlash = false;
    }

    //SHOWMEVIDEO FF can have AGENT or GUEST's video value in dual stream
    if (GLOBAL_MEETING_DATA.isAgent) {
        PublicVariables.featureFlag.SHOWMEVIDEO = PublicVariables.featureFlag?.SHOWMEDUALSTREAM === ENABLED ? data.SHOWMEHOSTVIDEO : DISABLE;
    } else {
        PublicVariables.featureFlag.SHOWMEVIDEO = urlParams.has(PARAMS.CAMERA) ? urlParams.get(PARAMS.CAMERA) : data.SHOWMEVIDEO;
    }

    PublicVariables.featureFlag.SHOWMEMICROPHONE = urlParams.has(PARAMS.MICROPHONE) ? urlParams.get(PARAMS.MICROPHONE) : data.SHOWMEMICROPHONE;

    if (urlParams.has(PARAMS.GUEST_NAME)) {
        const paramGuestName = decodeURIComponent(urlParams.get(PARAMS.GUEST_NAME))?.substring(0, ENTER_GUEST_NAME_MAX_LENGTH);
        CALL_DATA.participantUserName = paramGuestName;
    }

    // locationEnabled will be local participant's flag
    PublicVariables.featureFlag.locationEnabled = GLOBAL_MEETING_DATA.isAgent ? data.AGENTLOCATION : data.ENABLELOCATION;
};

/* check user is silent observer or not */
export const isSilentObserver = (identity) => {
    return identity?.includes("_observer");
};

/* Show zoom button or not */
export const showZoomButton = () => {
    return !PublicVariables.isOnlyUploadImageMeet && PublicVariables.featureFlag.SHOWMEZOOM === ENABLED;
};

export const initialRecordingOff = () => {
    return PublicVariables.featureFlag.SHOWMERECORD == RECORDING.INITOFF;
};

export const isAgentIdentity = (identity) => {
    return identity?.indexOf("_user") > -1;
};

export const empty = (value) => {
    if (value === undefined || value === null) {
        return true;
    }
    const varType = typeof value;
    switch (varType) {
        case "object":
            return Object.keys(value).length === 0;
        case "string":
            return !value.trim();
        case "boolean":
            return !value;
        case "number":
            return value === 0;
        default:
            return false;
    }
};

export const totalTimeInSeconds = (timeString) => {
    if (!timeString) return 0;

    const timeComponents = timeString.split(":");
    const hours = timeComponents.length === 3 ? parseInt(timeComponents[0]) : 0;
    const minutes = parseInt(timeComponents[timeComponents.length === 3 ? 1 : 0]);
    const seconds = parseInt(timeComponents[timeComponents.length === 3 ? 2 : 1]);

    return hours * 3600 + minutes * 60 + seconds;
};

export const isSafariBelow14 = () => {
    const browserName = DetectRTC.browser.name.toLowerCase();
    const isSafari = DetectRTC.osName.toLowerCase() === "ios" || DetectRTC.osName.toLowerCase() === "mac os x";
    const isSafariBrowser = browserName === "safari";

    return isSafari && isSafariBrowser && DetectRTC.browser.version < 14;
};

export const isSafariBelow16 = () => {
    const browser = DetectRTC.browser.name.toLowerCase();
    const OSName = DetectRTC.osName.toLowerCase();
    // iOS Edge uses WebKit engine, so it needs the same iOS version workarounds as Safari/Chrome
    const isiOSEdge = isIOSEdgeBrowser();
    if (
        (OSName === "ios" && (browser === "safari" || browser === "chrome" || isiOSEdge) && parseFloat(DetectRTC.osVersion) <= 16) ||
        (OSName === "mac os x" && browser === "safari" && DetectRTC.browser.version <= 16)
    ) {
        return true;
    } else {
        return false;
    }
};

export const isSafariBelow18 = () => {
    const browser = DetectRTC.browser.name.toLowerCase();
    const OSName = DetectRTC.osName.toLowerCase();
    // iOS Edge uses WebKit engine, so it needs the same iOS version workarounds as Safari/Chrome
    const isiOSEdge = isIOSEdgeBrowser();
    if (
        (OSName === "ios" && (browser === "safari" || browser === "chrome" || isiOSEdge) && parseFloat(DetectRTC.osVersion) < 18) ||
        (OSName === "mac os x" && browser === "safari" && DetectRTC.browser.version < 18)
    ) {
        return true;
    } else {
        return false;
    }
};

export const isMobileSafariBelow18 = () => {
    const browser = DetectRTC.browser.name.toLowerCase();
    const OSName = DetectRTC.osName.toLowerCase();
    // iOS Edge uses WebKit engine, so it needs the same iOS version workarounds as Safari/Chrome
    const isiOSEdge = isIOSEdgeBrowser();
    if (OSName === "ios" && (browser === "safari" || browser === "chrome" || isiOSEdge) && parseFloat(DetectRTC.osVersion) < 18) {
        return true;
    } else {
        return false;
    }
};

async function getAndroidVersion() {
    if (navigator.userAgentData) {
        const ua = await navigator.userAgentData.getHighEntropyValues(["platform", "platformVersion"]);
        const version = ua.platformVersion;
        const majorVersion = version.split(".")[0];
        return majorVersion;
    }
}

export const checkAndroidOS = async () => {
    const isBelow15 = await isAndroidMobileOSBelow15();
    GLOBAL_MEETING_DATA.androidOSBelow15 = isBelow15;
    LogFirebase("VirtualBackground", "isAndroidOSBelow15", isBelow15);
};

export const isAndroidMobileOSBelow15 = async () => {
    const OSName = DetectRTC.osName.toLowerCase();
    if (OSName === "android") {
        const version = await getAndroidVersion();
        if (parseFloat(version) < 15) {
            return true;
        } else {
            return false;
        }
    }
    return false;
};

export const isiPhoneOnlyDevice = () => {
    const browserName = DetectRTC.browser.name.toLowerCase();
    const isIOS = DetectRTC.osName.toLowerCase() === "ios";
    const isSupportedBrowser = ["safari", "chrome"].includes(browserName);

    return isIOS && isSupportedBrowser;
};

/* get language from local storage */
const getParamsFromUrl = function (url) {
    const finalParams = { id: null, m: null, c: null, s: null, d: null };

    if (url.includes("?")) {
        const queryString = url.split("?")[1];
        const paramsArr = queryString.split("&");

        paramsArr.forEach((param) => {
            const [key, val] = param.split("=");
            if (key && val) {
                finalParams[key] = decodeURIComponent(val); // Decode URI components for safety
            }
        });

        console.log(finalParams);
    } else {
        console.log("NO PARAMS IN URL");
    }

    return finalParams;
};

/* set language into local storage */
export const getLanguageFromStorage = function () {
    let lang = null;
    const urlParams = getParamsFromUrl(window.location.href);
    if (urlParams?.id && storage.get(LOCAL_STORAGE_KEYS.USER_LANGUAGE)) {
        //this will use ShowMe_Agent > blitz.usersLanguage which is encoded
        lang = storage.get(LOCAL_STORAGE_KEYS.USER_LANGUAGE);
    } else {
        //direct uses angular funcion - will be used for guests
        lang = localStorage.getItem("usersLanguage");
    }
    if (lang) {
        console.log(" Language in storage = ", lang);
        return lang;
    }
    // Return null if no language is found
    return null;
};

export const setLanguageInStorage = function (language) {
    const languageCode = language?.languageCode;
    const urlParams = getParamsFromUrl(window.location.href);
    const existingLanguage = storage.get(LOCAL_STORAGE_KEYS.USER_LANGUAGE);

    if (urlParams.id && existingLanguage) {
        // Store in ShowMe_Agent > Blitzz.usersLanguage
        storage.set(LOCAL_STORAGE_KEYS.USER_LANGUAGE, languageCode);
        console.log("::AGENTS local storage set=" + storage.get(LOCAL_STORAGE_KEYS.USER_LANGUAGE));
    } else {
        const storageType = localStorage.getItem("storagetype");

        if (storageType === "localStorage") {
            localStorage.setItem("usersLanguage", languageCode);
        } else {
            // Set localStorage as the default storage type
            localStorage.setItem("storagetype", "localStorage");
            localStorage.setItem("usersLanguage", languageCode);
        }
        console.log("::local storage set=" + localStorage.getItem("usersLanguage"));
    }
};

export const getSessionId = (sessionId) => {
    let getSessionId = null;
    const searchParams = new URLSearchParams(window.location.search);
    const hasSessionId = searchParams.get("code");

    if (sessionId) {
        // for normal URL(All domains except the Xiaomi domain.)
        getSessionId = sessionId;
    } else if (hasSessionId) {
        // for Xiaomi domain
        getSessionId = hasSessionId;
    }

    return getSessionId;
};

export const isIOSOrMacSafariImageBlobIssue = () => {
    const isIOSMobile = PublicVariables.isMobile && PublicVariables.isIOSDevice;
    const isMacSafari = NavigatorService.isMac() && NavigatorService.isSafariBrowser();

    return isIOSMobile || isMacSafari;
};
